<template>
  <div class="audit-staff-manage-page">
    <div class="figma-white-card">
      <!-- 页面标题栏：面包屑 -->
      <div class="figma-page-header">
        <div class="figma-breadcrumb">
          <span class="breadcrumb-parent breadcrumb-link" @click="goContractType">{{ $t('auditStaffManage.breadcrumb.contractType') }}</span>
          <span class="breadcrumb-separator">&gt;</span>
          <span class="breadcrumb-current">{{ $t('auditStaffManage.breadcrumb.current') }}</span>
        </div>
      </div>

      <!-- 搜索区域 -->
      <div class="figma-search-row">
        <div class="figma-search-group">
          <el-button size="small" class="figma-btn-add" @click="handleAddWorkflow">
            <i class="el-icon-plus"></i>{{ $t('auditStaffManage.action.addWorkflow') }}
          </el-button>
        </div>
        <div class="figma-search-group">
          <el-input
            v-model.trim="searchForm.flowId"
            size="small"
            :placeholder="$t('auditStaffManage.search.flowIdPlaceholder')"
            class="figma-search-input"
            clearable
            @keyup.enter.native="handleSearch"
          />
          <el-select
            v-model="searchForm.flowType"
            size="small"
            :placeholder="$t('auditStaffManage.search.flowTypePlaceholder')"
            class="figma-search-input"
            clearable
            @change="handleSearch"
          >
            <el-option v-for="item in flowTypes" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
          </el-select>
          <el-input
            v-model.trim="searchForm.flowName"
            size="small"
            :placeholder="$t('auditStaffManage.search.flowNamePlaceholder')"
            class="figma-search-input"
            clearable
            @keyup.enter.native="handleSearch"
          />
          <el-button size="small" class="figma-btn-search" @click="handleSearch">
            <i class="el-icon-search"></i>
            <span>{{ $t('auditStaffManage.search.search') }}</span>
          </el-button>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="figma-table-section">
        <el-table
          v-loading="loading"
          :data="tableData"
          style="width: 100%"
          :header-cell-style="headerCellStyle"
          :cell-style="cellStyle"
        >
          <el-table-column prop="flowId" :label="$t('auditStaffManage.table.flowId')" min-width="180" show-overflow-tooltip />
          <el-table-column prop="flowName" :label="$t('auditStaffManage.table.flowName')" min-width="240" show-overflow-tooltip />
          <el-table-column prop="flowTypeName" :label="$t('auditStaffManage.table.flowType')" min-width="240" show-overflow-tooltip />
          <el-table-column prop="createTime" :label="$t('auditStaffManage.table.createTime')" min-width="200" show-overflow-tooltip />
          <el-table-column :label="$t('auditStaffManage.table.action')" min-width="160">
            <template slot-scope="scope">
              <div class="action-links">
                <a class="action-link action-link-blue" @click="handleEdit(scope.row)">{{ $t('auditStaffManage.operation.setting') }}</a>
                <span class="link-space"></span>
                <a class="action-link action-link-green" @click="handleViewImage(scope.row)">{{ $t('auditStaffManage.operation.viewImage') }}</a>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <div class="figma-pagination">
        <span class="pagination-total">共{{ pagination.total }}条</span>
        <el-pagination
          :current-page="pagination.page"
          :page-size="pagination.row"
          layout="prev, pager, next"
          :total="pagination.total"
          @current-change="handlePageChange"
        />
        <el-select v-model="pagination.row" size="small" class="pagination-size-select" @change="handleSizeChange">
          <el-option label="10条/页" :value="10" />
          <el-option label="20条/页" :value="20" />
          <el-option label="50条/页" :value="50" />
        </el-select>
      </div>
    </div>

    <!-- 添加流程弹框 -->
    <el-dialog
      :title="$t('auditStaffManage.addDialog.title')"
      :visible.sync="addDialogVisible"
      width="650px"
      custom-class="add-workflow-dialog"
      :close-on-click-modal="false"
      @close="handleDialogClose"
    >
      <el-form
        ref="addForm"
        :model="addForm"
        :rules="addFormRules"
        label-width="120px"
        label-position="right"
        class="add-workflow-form-body"
      >
        <el-form-item :label="$t('auditStaffManage.addDialog.flowName') + '：'" prop="flowName" required>
          <el-input v-model="addForm.flowName" :placeholder="$t('auditStaffManage.addDialog.flowNamePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('auditStaffManage.addDialog.flowType') + '：'" prop="flowType" required>
          <el-select v-model="addForm.flowType" style="width: 100%" :placeholder="$t('auditStaffManage.addDialog.flowTypePlaceholder')">
            <el-option v-for="item in flowTypes" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('auditStaffManage.addDialog.describle') + '：'">
          <el-input v-model="addForm.describle" type="textarea" :placeholder="$t('auditStaffManage.addDialog.describlePlaceholder')" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="add-workflow-footer">
        <el-button class="add-workflow-btn-cancel" @click="addDialogVisible = false">{{ $t('auditStaffManage.addDialog.cancel') }}</el-button>
        <el-button class="add-workflow-btn-confirm" :loading="addSubmitting" @click="handleAddSubmit">{{ $t('auditStaffManage.addDialog.confirm') }}</el-button>
      </span>
    </el-dialog>

    <!-- 流程图查看 -->
    <view-image ref="viewImage" />

    <!-- 流程设置弹框 -->
    <workflow-setting-dialog ref="workflowSettingDialog" @success="getList" />
  </div>
</template>

<script>
import { listWorkflows, listWorkflowImage, saveWorkflow } from '@/api/system/workflowManageApi'
import { getDict, getCommunityId } from '@/api/community/communityApi'
import ViewImage from '@/components/system/viewImage'
import WorkflowSettingDialog from '@/components/contract/workflowSettingDialog'

export default {
  name: 'AuditStaffManageList',
  components: {
    ViewImage,
    WorkflowSettingDialog
  },
  data() {
    return {
      loading: false,
      searchForm: {
        flowId: '',
        flowName: '',
        flowType: ''
      },
      flowTypes: [],
      tableData: [],
      pagination: {
        total: 0,
        page: 1,
        row: 10
      },
      communityId: '',
      addDialogVisible: false,
      addSubmitting: false,
      addForm: {
        flowName: '',
        flowType: '',
        describle: ''
      },
      addFormRules: {
        flowName: [
          { required: true, message: this.$t('auditStaffManage.validate.flowNameRequired'), trigger: 'blur' }
        ],
        flowType: [
          { required: true, message: this.$t('auditStaffManage.validate.flowTypeRequired'), trigger: 'change' }
        ]
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.initPage()
  },
  methods: {
    headerCellStyle() {
      return {
        background: '#EBF2FA',
        color: '#364153',
        fontSize: '14px',
        fontWeight: '400',
        fontFamily: 'PingFang SC',
        height: '50px',
        padding: '0',
        textAlign: 'left'
      }
    },
    cellStyle({ row }) {
      const rowIndex = this.tableData.indexOf(row)
      const base = {
        color: '#1D2129',
        fontSize: '14px',
        fontFamily: 'PingFang SC',
        height: '50px',
        padding: '0',
        fontWeight: rowIndex % 2 === 1 ? '500' : '400',
        textAlign: 'left'
      }
      return rowIndex % 2 === 1 ? { ...base, background: '#F8FBFF' } : { ...base, background: 'white' }
    },
    async initPage() {
      await this.getDictData()
      this.getList()
    },
    async getDictData() {
      try {
        const data = await getDict('workflow', 'flow_type')
        this.flowTypes = data || []
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    getListArray(res) {
      if (Array.isArray(res)) return res
      if (Array.isArray(res && res.data)) return res.data
      if (Array.isArray(res && res.records)) return res.records
      if (Array.isArray(res && res.rows)) return res.rows
      return []
    },
    getTotal(res, list) {
      return Number((res && (res.total || res.count || res.totalCount)) || list.length || 0)
    },
    normalizeRow(row) {
      return {
        ...row,
        flowId: row.flowId || '',
        flowName: row.flowName || '',
        flowTypeName: row.flowTypeName || row.flowType || '',
        createTime: row.createTime || ''
      }
    },
    async getList() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          row: this.pagination.row,
          communityId: this.communityId,
          ...this.searchForm
        }
        const res = await listWorkflows(params)
        const list = this.getListArray(res)
        this.tableData = list.map(item => this.normalizeRow(item))
        this.pagination.total = this.getTotal(res, list)
      } catch (error) {
        this.$message.error(this.$t('auditStaffManage.message.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.getList()
    },
    goContractType() {
      this.$router.push('/pages/committee/contractType')
    },
    handleEdit(row) {
      this.$refs.workflowSettingDialog.open({ ...row, communityId: this.communityId })
    },
    async handleViewImage(row) {
      try {
        const params = {
          communityId: this.communityId,
          flowId: row.flowId
        }
        const { data } = await listWorkflowImage(params)
        this.$refs.viewImage.open({
          url: 'data:image/png;base64,' + data
        })
      } catch (error) {
        console.error('获取流程图片失败:', error)
        this.$message.error(this.$t('auditStaffManage.message.imageError'))
      }
    },
    handlePageChange(page) {
      this.pagination.page = page
      this.getList()
    },
    handleSizeChange(size) {
      this.pagination.row = size
      this.pagination.page = 1
      this.getList()
    },
    handleAddWorkflow() {
      this.addForm = { flowName: '', flowType: '', describle: '' }
      this.addDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.addForm) this.$refs.addForm.clearValidate()
      })
    },
    handleDialogClose() {
      if (this.$refs.addForm) this.$refs.addForm.resetFields()
      this.addForm = { flowName: '', flowType: '', describle: '' }
    },
    async handleAddSubmit() {
      const valid = await this.$refs.addForm.validate().catch(() => false)
      if (!valid) return
      try {
        this.addSubmitting = true
        await saveWorkflow({
          ...this.addForm,
          communityId: this.communityId
        })
        this.$message.success(this.$t('auditStaffManage.message.addSuccess'))
        this.addDialogVisible = false
        this.getList()
      } catch (error) {
        this.$message.error(this.$t('auditStaffManage.message.addError'))
      } finally {
        this.addSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.audit-staff-manage-page {
  height: calc(100vh - 60px);
  background: #EBF2FA;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.figma-white-card {
  background: white;
  border-radius: 0 0 12px 12px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 页面标题栏 */
.figma-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px;
  flex-shrink: 0;
}

.figma-breadcrumb {
  display: flex;
  align-items: center;
  gap: 4px;
}

.breadcrumb-parent {
  font-size: 16px;
  font-weight: 600;
  color: #8C9BB4;
  font-family: 'PingFang SC';
  line-height: 22px;
}

.breadcrumb-separator {
  font-size: 16px;
  font-weight: 600;
  color: #8C9BB4;
  font-family: 'PingFang SC';
  line-height: 22px;
  margin: 0 4px;
}

.breadcrumb-current {
  font-size: 16px;
  font-weight: 600;
  color: #1D2129;
  font-family: 'PingFang SC';
  line-height: 22px;
}

.breadcrumb-link {
  cursor: pointer;
}

.breadcrumb-link:hover {
  color: #0E76FF;
}

/* 添加流程按钮 */
.figma-search-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 24px 12px;
  flex-shrink: 0;
}

.figma-btn-add {
  height: 38px;
  border-radius: 10px !important;
  background: #0E76FF !important;
  border: none !important;
  color: white !important;
  font-size: 14px;
  font-weight: 500;
  font-family: 'PingFang SC';
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.figma-btn-add:hover {
  background: #1C99FF !important;
}

/* 搜索区域 - figma-search-row 已在上方定义 */

.figma-search-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.figma-search-input {
  width: 256px;
  height: 38px;
}

.figma-search-input >>> .el-input__inner {
  border-radius: 10px;
  border: none;
  outline: 1px #DCE6F5 solid;
  outline-offset: -1px;
  height: 38px;
  font-size: 14px;
  background: white;
  color: #1D2129;
}

.figma-search-input >>> .el-input__inner:focus {
  outline-color: #0E76FF;
}

.figma-search-input >>> .el-input__inner::placeholder {
  color: #8C9BB4;
  font-family: 'Inter', 'PingFang SC';
}

.figma-btn-search {
  width: 84px;
  height: 38px;
  border-radius: 10px !important;
  border: none !important;
  outline: 1px #0E76FF solid !important;
  outline-offset: -1px;
  color: #0E76FF !important;
  background: white !important;
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.figma-btn-search:hover {
  background: rgba(14, 118, 255, 0.08) !important;
}

/* 表格 */
.figma-table-section {
  flex: 1;
  min-height: 0;
  padding: 0 24px;
  overflow: auto;
}

.figma-table-section >>> .el-table {
  border-radius: 8px;
  overflow: hidden;
  border: none;
}

.figma-table-section >>> .el-table::before {
  display: none;
}

.figma-table-section >>> .el-table__header th {
  background: #EBF2FA !important;
  border: none !important;
}

.figma-table-section >>> .el-table__body td {
  border: none !important;
}

.figma-table-section >>> .el-table th .cell,
.figma-table-section >>> .el-table td .cell {
  padding-left: 24px;
  padding-right: 24px;
  line-height: 22px;
  text-align: left;
}

/* 操作链接 */
.action-links {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
}

.action-link {
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;
  font-weight: 400;
  font-family: 'PingFang SC';
}

.action-link:hover {
  opacity: 0.85;
}

.action-link-blue {
  color: #0E76FF;
}

.action-link-green {
  color: #21B956;
}

.link-space {
  width: 16px;
  display: inline-block;
}

/* 分页 */
.figma-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 24px 24px;
  gap: 8px;
  flex-shrink: 0;
}

.pagination-total {
  font-size: 14px;
  color: #1D2129;
  margin-right: 4px;
  font-family: 'PingFang SC';
}

.figma-pagination >>> .el-pagination {
  display: flex;
  align-items: center;
  padding: 0;
}

.figma-pagination >>> .btn-prev,
.figma-pagination >>> .btn-next {
  width: 32px;
  height: 32px;
  border-radius: 2px;
  background: white;
}

.figma-pagination >>> .el-pager li {
  min-width: 32px;
  height: 32px;
  line-height: 32px;
  border-radius: 8px;
  color: #1D2129;
  font-weight: 600;
}

.figma-pagination >>> .el-pager li.active {
  background: #E8F3FF;
  color: #0E76FF;
}

.pagination-size-select {
  width: 92px;
  margin-left: 8px;
}

.pagination-size-select >>> .el-input__inner {
  height: 32px;
  border-radius: 8px;
  background: #F2F3F5;
  border: none;
  font-size: 14px;
  color: #1D2129;
}

/* 添加流程弹窗样式 */
.audit-staff-manage-page >>> .add-workflow-dialog { border-radius: 12px; overflow: hidden; }
.audit-staff-manage-page >>> .add-workflow-dialog .el-dialog__header {
  height: 66px; box-sizing: border-box; padding: 22px 24px;
  background: #EBF2FA; border-bottom: 1px solid #DCE6F5;
}
.audit-staff-manage-page >>> .add-workflow-dialog .el-dialog__title {
  font-size: 16px; color: #1D2129; font-weight: 600;
}
.audit-staff-manage-page >>> .add-workflow-dialog .el-dialog__body {
  padding: 24px 24px 8px;
}
.audit-staff-manage-page >>> .add-workflow-dialog .el-dialog__footer {
  padding: 8px 24px 28px;
  display: flex; justify-content: flex-end; gap: 12px;
}
.add-workflow-form-body .el-form-item { margin-bottom: 20px; }
.add-workflow-form-body .el-form-item:last-child { margin-bottom: 0; }
.add-workflow-form-body >>> .el-form-item { display: flex; align-items: flex-start; }
.add-workflow-form-body >>> .el-form-item__label {
  font-size: 14px; font-weight: 500; color: #1D2129;
  line-height: 40px; height: 40px; float: none;
}
.add-workflow-form-body >>> .el-form-item__content {
  flex: 1; margin-left: 0 !important; line-height: 40px;
}
.add-workflow-form-body >>> .el-input__inner {
  border-radius: 10px; border: 1px solid #DCE6F5;
  font-size: 14px; color: #1D2129; height: 40px;
}
.add-workflow-form-body >>> .el-input__inner:focus {
  border-color: #0E76FF;
}
.add-workflow-form-body >>> .el-textarea__inner {
  border-radius: 10px; border: 1px solid #DCE6F5;
  font-size: 14px; color: #1D2129;
}
.add-workflow-form-body >>> .el-textarea__inner:focus {
  border-color: #0E76FF;
}
.add-workflow-footer { display: flex; justify-content: flex-end; gap: 12px; }
.add-workflow-btn-cancel {
  width: 98px; height: 38px; border-radius: 10px !important;
  color: #0E76FF !important; border-color: #0E76FF !important;
  background: white !important;
}
.add-workflow-btn-confirm {
  width: 98px; height: 38px; border-radius: 10px !important;
  background: #0E76FF !important; color: white !important;
  border-color: #0E76FF !important;
}

@media (max-width: 1024px) {
  .figma-page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .figma-search-row {
    justify-content: flex-start;
  }
  .figma-search-group {
    flex-wrap: wrap;
    width: 100%;
  }
  .figma-search-input {
    width: min(256px, calc(100vw - 150px));
  }
}
</style>
